import {Card, Dropdown, Empty, Menu, Spin, Tag} from 'antd';
import {useEffect, useState} from 'react';
import DateObject from 'react-date-object';
import {toast} from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {RQGetAllUserOrderList, RqUpdateOrder} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';

// eslint-disable-next-line react/prop-types
const OrderSummary = ({userId}) => {
  const [orders, setOrders] = useState([]);

  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;
  const updateOrderMutation = RqUpdateOrder();

  const {data: userData, isLoading} = RQGetAllUserOrderList(userId, token);

  const handleSave = async (orderId, newStatus) => {
    const orderToUpdate = orders.find(o => o.orderId === orderId);
    if (!orderToUpdate) {return;}

    const rawOrder = orderToUpdate._rawOrder;

    const formData = {
      ...rawOrder,          
      status: newStatus,  
    };

    try {
      await updateOrderMutation.mutateAsync({
        token,
        formData,
        id: orderId,
      });

      toast.success('Order Status updated successfully');

      // Update local UI only
      setOrders(prev =>
        prev.map(order =>
          order.orderId === orderId
            ? {
              ...order,
              status: newStatus,
              _rawOrder: {
                ...order._rawOrder,
                status: newStatus,
              },
            }
            : order,
        ),
      );
    } catch (err) {
      console.error('Failed to update:', err);
      toast.error('Failed to update order status');
    }
  };

  const toDropboxRawUrl = (url = '') =>
    url.replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');

  useEffect(() => {
    if (userData && userData.length > 0) {
      const sortedOrders = [...userData].sort(
        (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
      );

      const formattedOrders = sortedOrders.map(order => {
        const rawDate = order.createdOn;
        const dateObj = rawDate ? new DateObject({date: rawDate}) : null;
        const formattedDate = dateObj ? dateObj.format('DD-MM-YYYY') : 'N/A';

        const products = order.products.map(product => {
          // Variant image logic
          const variants = product.productInfo?.variants || [];
          const matchedVariant = variants.find(
            v => v.color === product.color,
          );

          const variantImage =
            matchedVariant?.images?.[0] ||
            product.productInfo?.imageUrl?.[0] ||
            '';

          // Price / Color / Discount
          const unitPrice =
            Number(product.pricePerUnit) ||
            Number(product.productInfo?.price) ||
            0;

          const selectedColor =
            product.color ||
            matchedVariant?.color ||
            'N/A';

          const productDiscount =
            Number(product.productInfo?.discount) || 0;

          const promoDiscount =
            Number(order.orderPromoCodeInfo?.discountValue) ||
            Number(product.productInfo?.promocodeInfo?.discountValue) ||
            0;

          const finalDiscount = promoDiscount || productDiscount;

          return {
            key: `${order.id}-${product.productId}`,
            productName: product.productInfo?.productName || 'Unknown',
            imageUrl: variantImage,

            pricePerUnit: unitPrice,
            selectedColor,
            discount: finalDiscount,

            productQuantity: product.quantity || 0,
          };
        });

        return {
          orderId: order.id,
          status: order.status || 'UNKNOWN',
          date: formattedDate,
          totalAmount: order.totalAmount,
          deliveryCharges: order.deliveryCharges,
          products,

          _rawOrder: order,
        };
      });

      setOrders(formattedOrders);
    } else {
      setOrders([]);
    }
  }, [userData]);

  if (isLoading) {
    return (
      <div className={'flex justify-center py-10'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div className={'p-6'}>
        <h1 className={'header'}>Order History</h1>
        <Empty description={'Not bought anything'} />
      </div>
    );
  }

  return (
    <div className={'py-6 px-0 flex flex-col gap-6'}>
      <h1 className={'header'}>Order History</h1>

      {orders.map(order => (
        <Card key={order.orderId} className={'shadow-md'}>

          {/* HEADER */}
          <div className={
            'flex md:flex-row flex-col md:justify-between md:items-center mb-3 gap-2'
          }>
            <div className={'flex flex-col'}>
              <div className={'flex gap-1'}>
                <p className={'font-semibold'}>Date:</p>
                <p>{order.date}</p>
              </div>

              <p className={'text-sm font-medium text-gray-600'}>
        Payment: {order._rawOrder?.products?.[0]?.paymentMethod === 'COD'
                  ? 'Cash on Delivery'
                  : 'Paid'}
              </p>
            </div>

            <Tag
              color={
                order.status === 'CONFIRMED'
                  ? 'green'
                  : order.status === 'PENDING'
                    ? 'orange'
                    : order.status === 'CANCELLED'
                      ? 'red'
                      : order.status === 'SHIPPED'
                        ? 'blue'
                        : order.status === 'DELIVERED'
                          ? 'purple'
                          : 'default'
              }
            >
              {order.status}
            </Tag>
          </div>

          <div className={'flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-1'}>

            {order.products.map(product => {
              const pricePerUnit = Number(product.pricePerUnit) || 0;
              const discountPercent = Number(product.discount) || 0;

              const discountedPrice =
        pricePerUnit - (pricePerUnit * discountPercent) / 100;

              return (
                <div
                  key={product.key}
                  className={
                    'flex items-start md:items-center gap-4 border-b pb-3 border-primary-50 last:border-b-0'
                  }
                >

                  {/* IMAGE */}
                  <img
                    src={toDropboxRawUrl(product.imageUrl)}
                    alt={product.productName}
                    className={
                      'w-16 h-16 object-cover rounded border border-primary-100'
                    }
                  />

                  <div className={
                    'flex flex-col md:flex-row w-full md:justify-between'
                  }>

                    {/* PRODUCT NAME */}
                    <div className={'md:flex-1'}>
                      <p className={'font-semibold'}>
                        {product.productName}
                      </p>
                    </div>

                    <div className={'mt-2 md:mt-0 overflow-x-auto'}>
                      <div className={'flex gap-6 min-w-max'}>

                        <div className={'text-sm'}>
                          <p className={'font-bold'}>Qty</p>
                          <p>{product.productQuantity}</p>
                        </div>

                        <div className={'text-sm text-center'}>
                          <p className={'font-bold'}>Color</p>
                          <p>{product.selectedColor}</p>
                        </div>

                        <div className={'text-sm text-center'}>
                          <p className={'font-bold'}>MRP</p>
                          <p>₹ {pricePerUnit}</p>
                        </div>

                        <div className={'text-sm text-center'}>
                          <p className={'font-bold'}>Discounted Price</p>
                          <p>₹ {discountedPrice}</p>
                        </div>

                      </div>
                    </div>

                  </div>
                </div>
              );
            })}

          </div>

          {/* FOOTER */}
          <div className={
            'flex justify-end mt-2 pt-2 border-t border-primary-100'
          }>
            <p className={'text-base font-bold'}>
      Total: ₹ {order.totalAmount}
            </p>
          </div>

        </Card>
      ))}
    </div>
  );
};

export default OrderSummary;
